import { useEffect, useRef, useState } from 'react'
import tw, { GlobalStyles } from 'twin.macro'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from '@/utils/useTranslation'

import { Global } from '@emotion/react'
import { useLocation } from '@reach/router'

import Link from '@/components/link-component'
import all from '@/components/all-global'
import Meta from '@/components/meta'
import Footer from '@/components/footer'
import HelpHeader from '@/components/help-header'
import HelpSearch from '@/components/help-search'
import HelpNav from '@/components/help-nav'
import TocNav from '@/components/toc-nav'
import ContactSupportForm from '@/components/forms/contact-support'
import routes from '@/constants/routes'
import useGetPdf from '@/utils/useGetPdf'
import CloudStatus from '@/components/cloud-status'
import NavContainer from '@/components/nav-container'
import Cta from '@/components/section/cta'
import { isPrint } from '@/utils/print'

function BackgroundPulse({ isActive = false, isAlt = false, delay = 0 }) {
  return (
    <motion.div
      css={[
        tw`absolute top-0 left-1/2 z-[1] w-[110%] h-full rounded-3xl pointer-events-none`,
        isAlt ? tw`bg-primaryBlue/10` : tw`bg-white/5`,
      ]}
      initial="hidden"
      animate={isActive ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0, translateX: '-50%' },
        visible: {
          scaleX: [0.88, 1.03, 1.07],
          scaleY: [0.98, 1.03, 1.07],
          opacity: [0, 1, 0],
        },
      }}
      transition={{ duration: 1.25, ease: 'linear', repeat: 3, delay }}
    />
  )
}

export default function HelpParent({
  children,
  title,
  seo = {},
  otherLocales = [],
  outlineBody,
  helpNavNodes,
  helpNavInitial = [],
  promoCta = null,
}) {
  const headerRef = useRef(null)
  const [isNavOpen, setNavOpen] = useState(false)
  const { hash } = useLocation()
  const { t } = useTranslation()

  const { getPdf, isLoading } = useGetPdf()

  const [helpContactFormOpen, setHelpContactFormOpen] = useState(false)
  const supportRef = useRef(null)
  const [flash, setFlash] = useState(0)

  const print = isPrint()

  useEffect(() => {
    if (hash === '#contact-support') {
      setHelpContactFormOpen(true)
      setFlash(1)
    }
  }, [])

  useEffect(() => {
    if (helpContactFormOpen) supportRef.current.scrollIntoView({ behavior: 'smooth', alignToTop: false })
  }, [helpContactFormOpen])

  return (
    <>
      <Meta {...seo} otherLocales={otherLocales} />
      <GlobalStyles />
      <Global styles={all} />
      <HelpHeader ref={headerRef} hasLeftLinks={false} hasSearch={false} twRoot={tw`bg-indigoBlue`} />
      <main tw="mb-16">
        <button
          type="button"
          tw="z-10 sticky top-[64px] w-full bg-white/80 backdrop-blur-sm py-2 text-sm font-bold truncate border-b md:(hidden) [-webkit-tap-highlight-color: rgba(0, 0, 0, 0)]"
          onClick={() => setNavOpen(!isNavOpen)}
        >
          {helpNavInitial?.length > 0 ? (
            helpNavInitial
              .map((nav) => nav.title)
              .reduce((prev, curr) => [prev, <span tw="font-bwi mx-[2px]">&#xe91b;</span>, curr])
          ) : (
            <>
              Help menu <span tw="font-bwi mx-[2px]">&#xe91b;</span>
            </>
          )}
        </button>
        <div tw="hidden md:([display: unset])">
          <HelpSearch />
        </div>

        <div
          tw="flex flex-col-reverse (md:flex-row) max-w-[1600px] mx-auto mb-6 px-5 relative"
          css={[print && tw`hidden`]}
        >
          <div>
            <NavContainer
              position="left"
              isDrawerOpen={isNavOpen}
              onRequestClose={() => {
                setNavOpen(false)
              }}
              tw="md:(sticky top-20 -mt-44 bg-gray-100 rounded-4xl)"
              headerRef={headerRef}
            >
              <HelpNav {...{ helpNavNodes, helpNavInitial }} />
            </NavContainer>
          </div>
          <div>
            <div tw="grid grid-cols-[minmax(0, 1fr)] md:pl-6 lg:(grid-cols-[minmax(0, 1fr) 230px] pl-0)">
              <article tw="order-2 sm:(mr-5) md:(ml-6 mr-6) lg:(order-1 ml-10 mr-10)">
                {helpNavInitial?.length > 0 && (
                  <div tw="hidden py-2 mt-1 lg:mt-4 text-primaryBlue text-sm font-bold md:(block)">
                    {helpNavInitial
                      .map((nav) => nav.title)
                      .reduce((prev, curr): any => [prev, <span tw="font-bwi mx-[2px]">&#xe91b;</span>, curr])}
                  </div>
                )}
                {children}
              </article>
              <div tw="order-1 lg:order-2">
                <ul tw="text-primaryBlue my-3 list-none" color="primaryBlue">
                  <li>
                    <span tw="font-bwi text-sm cursor-pointer mr-1">&#xe928;</span>
                    <Link to="/download/">{t('Download Bitwarden')}</Link>
                  </li>
                  <li>
                    <span tw="font-bwi text-sm cursor-pointer mr-1">&#xe965;</span>
                    <Link to="https://community.bitwarden.com/t/about-the-feature-requests-category">
                      {t('Feature Request')}
                    </Link>
                  </li>
                  <li>
                    <span tw="font-bwi text-sm cursor-pointer mr-1">&#xe963;</span>
                    <Link to="https://community.bitwarden.com/c/support/">{t('Ask the Community')}</Link>
                  </li>
                  <li>
                    <span tw="font-bwi text-sm cursor-pointer mr-1">&#xe963;</span>

                    <Link to="/learning/">{t('Learning Center')}</Link>
                  </li>
                  <li>
                    <span tw="font-bwi text-sm cursor-pointer mr-1">&#xe934;</span>
                    <Link to={routes.contributorGuidelines}>{t('Contributor Guidelines')}</Link>
                  </li>
                </ul>

                {outlineBody !== undefined && (
                  <button
                    type="button"
                    disabled={isLoading}
                    tw="w-[210px] text-left border-2 bg-primaryBlue text-white rounded-full block px-4 py-2 my-8 whitespace-nowrap transition-colors hover:(bg-indigoBlue) disabled:(bg-gray-600)"
                    onClick={() => getPdf()}
                  >
                    {isLoading ? (
                      <span tw="font-bwi mr-2 animate-spin">&#xe937;</span>
                    ) : (
                      <span tw="font-bwi mr-2">&#xe94e;</span>
                    )}

                    {t('Download as PDF')}
                  </button>
                )}

                {!helpContactFormOpen && (
                  <button
                    type="button"
                    tw="w-[210px] text-left border-2 border-primaryBlue text-primaryBlue rounded-full block px-4 py-2 my-8 transition-colors hover:(bg-gray-100)"
                    onClick={() => setHelpContactFormOpen(!helpContactFormOpen)}
                  >
                    <span tw="font-bwi mr-2">&#xe91b;</span>

                    {t('Contact support')}
                  </button>
                )}
                <AnimatePresence>
                  {helpContactFormOpen && (
                    <motion.div
                      key={`contact-support-${flash}`}
                      ref={supportRef}
                      tw="relative"
                      initial="hidden"
                      animate={!helpContactFormOpen ? 'hidden' : 'visible'}
                      exit="hidden"
                      variants={{
                        hidden: { height: 0, opacity: 0, pointerEvents: 'none' },
                        visible: { height: 'auto', opacity: 1, pointerEvents: 'auto' },
                      }}
                      transition={{ duration: hash === '#contact-support' ? 0 : 0.3 }}
                    >
                      <BackgroundPulse isActive={flash === 1} />
                      <BackgroundPulse isActive={flash === 1} delay={0.25} isAlt />
                      <BackgroundPulse isActive={flash === 1} delay={0.5} />
                      <BackgroundPulse isActive={flash === 1} delay={0.75} isAlt />
                      <div tw="mt-4 mb-12 z-[2]">
                        <ContactSupportForm {...{ helpNavInitial }} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <CloudStatus tw="rounded-2xl p-6 [h3]:text-lg [&*>div]:(!w-auto z-10)" />
                <div tw="sticky top-20 max-h-[calc(100vh-8rem)] overflow-y-auto">
                  <TocNav sections={outlineBody} {...{ title, helpNavInitial }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {promoCta && (
          <div tw="[&>section]:(max-w-[1600px]! px-5) my-10">
            <Cta {...promoCta} />
          </div>
        )}
      </main>
      <Footer otherLocales={otherLocales} />
    </>
  )
}
